import React, { useCallback, useEffect } from "react";
import {
  Grid,
  Row,
  Col,
  Button,
  ListGroup,
  ListGroupItem,
  Badge,
} from "react-bootstrap";
import { FormattedDate, FormattedNumber } from "react-intl";
import { makeAuthRequest } from "../../http";
import { useDispatch, useSelector } from "react-redux";
import { selectFullExportLink } from "../../selectors";
import { fetchProfile } from "../../actions";
import { useQuery } from "react-query";
import { LoadingIndicator } from "../common/loading-indicator";
import { COLORS } from "../../constants";

const Coupon = ({ coupon: { duration, amount_off, percent_off } }) => (
  <ul>
    <li>Duration: {duration}</li>
    {amount_off && (
      <li>
        Amount off:{" "}
        <FormattedNumber style="currency" value={amount_off / 100} />
      </li>
    )}
    {percent_off && (
      <li>
        Percentage off:{" "}
        <FormattedNumber style="percent" value={percent_off / 100} />
      </li>
    )}
  </ul>
);

export const PremiumExport = ({ exportLink }) => {
  const { status, data } = useQuery("/api/export-run?country=usa");

  if (status === "loading") return <LoadingIndicator />;

  const {
    created_at: createdAt,
    archive_size_bytes: archiveSize,
    number_of_securities: numberOfSecurities,
  } = data;

  return (
    <>
      <h4>Latest Archive Information</h4>
      <ListGroup>
        <ListGroupItem header="Last Export Completed At">
          <FormattedDate
            year="numeric"
            month="short"
            day="2-digit"
            hour="numeric"
            minute="numeric"
            value={createdAt}
          />
        </ListGroupItem>
        <ListGroupItem header="Number of Securities">
          <FormattedNumber value={numberOfSecurities} />
        </ListGroupItem>
        <ListGroupItem header="Archive Size">
          <FormattedNumber
            style="unit"
            value={Math.round(archiveSize / 1000)}
            unit="kilobyte"
            unitDisplay="narrow"
          />
        </ListGroupItem>
      </ListGroup>
      <div align="center" style={{ margin: "24px" }}>
        <Button id="download-export-button" type="submit" href={exportLink}>
          Download
        </Button>
      </div>
    </>
  );
};

export const Subscription = ({ onSubmitManage }) => {
  const { status, data } = useQuery("/api/payments/subscription-info");
  if (status === "loading") return <LoadingIndicator />;

  const subscription = data.subscriptions[0];
  const {
    cancel_at: cancelAt,
    canceled_at: canceledAt,
    current_period_end: currentPeriodEnd,
    start_date: startDate,
    discount,
    plan: { amount, currency },
  } = subscription;

  return (
    <>
      <h4>
        Current Subscription{" "}
        {discount != null && (
          <Badge style={{ background: COLORS.success }}>discounted</Badge>
        )}
      </h4>
      <ListGroup>
        <ListGroupItem header="Amount">
          <FormattedNumber
            style="currency"
            value={amount / 100}
            currency={currency}
          />
        </ListGroupItem>
        {discount?.coupon && (
          <ListGroupItem header={`Discount ${discount.coupon.name}`}>
            <Coupon coupon={discount.coupon} />
          </ListGroupItem>
        )}
        <ListGroupItem header="Subscription Started At">
          <FormattedDate value={new Date(startDate * 1000)} />
        </ListGroupItem>
        <ListGroupItem header="Current Period End">
          <FormattedDate value={new Date(currentPeriodEnd * 1000)} />
        </ListGroupItem>

        {canceledAt && (
          <ListGroupItem header="Subscription Canceled At">
            <FormattedDate value={new Date(canceledAt * 1000)} />
          </ListGroupItem>
        )}
        {cancelAt && (
          <ListGroupItem header="Access Expires At">
            <FormattedDate value={new Date(cancelAt * 1000)} />
          </ListGroupItem>
        )}
      </ListGroup>
      <div align="center" style={{ margin: "12px" }}>
        <Button
          id="checkout-and-portal-button"
          type="submit"
          onClick={onSubmitManage}
        >
          Manage your billing information
        </Button>
      </div>
    </>
  );
};

export const ManagePremium = () => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchProfile);
  }, []);
  const fullExportLink = useSelector(selectFullExportLink);

  const onSubmitManage = useCallback(async () => {
    const response = await makeAuthRequest().post(
      "/api/payments/create-portal-session"
    );
    if (response.status === 200) {
      window.location.replace(response.data.return_url);
    }
  }, []);

  return (
    <Grid>
      <Row>
        <Col xs={12} md={12}>
          <PremiumExport exportLink={fullExportLink} />
        </Col>
      </Row>
    </Grid>
  );
};
